import { Grid } from '@mui/material';
import { isEmpty, keys } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  acceptWorkAssignment,
  getAssignedJobs,
  getWorkAssignmentByCurrentRecruiterAndJob,
  ICandidate,
  IJob,
  IJobSearchFilterDTO,
  IUserDTO,
  JobAssignmentStatusFilter,
} from 'wisestep-api';
import {
  IWorkAssignment,
  JobSelection,
  WsDrawer,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useUserContext } from '../../context';
import JobAssignmentSelection from '../../features/conversationWidget/components/JobAssignmentSelection';
import { failed, warning, success } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';

import BulkSms from './BulkSms';

interface IJobSelectionState {
  isStateInitialized: boolean;
  tempSelectedJob: IJob | null;
  openJobAssignmentSelection: boolean;
  assignmentData: IWorkAssignment | null;
  assignmentToTeams: string[];
  assignmentByRecruiters: string[];
  assignedJobs: IJob[];
  loader: boolean;
}

export interface IBulkSmsDrawerProps {
  candidates: ICandidate[];
  defaultJob?: IJob | null;
  open: boolean;
  handleClose: () => void;
}

const BulkSmsDrawer = ({
  candidates,
  defaultJob = null,
  open,
  handleClose,
}: IBulkSmsDrawerProps) => {
  const [state, setState] = useState<IJobSelectionState>({
    isStateInitialized: false,
    tempSelectedJob: null,
    openJobAssignmentSelection: false,
    assignmentData: null,
    assignmentToTeams: [],
    assignmentByRecruiters: [],
    assignedJobs: [],
    loader: false,
  });

  const methods = useForm();

  const [jobCode, setJobCode] = useState<string | null>(defaultJob?.jobCode ?? null);

  const [jobs, setJobs] = useState<IJob[]>([]);

  const [selectedJob, setSelectedJob] = useState<IJob | null>(defaultJob);

  const [selectedTitle, setSelectedTitle] = useState<string>();

  const [tempJob, setTempJob] = useState<IJob | null>(null);

  const [totalJobsCount, setTotalJobsCount] = useState<number>(0);

  const user = useUserContext().userDTO as IUserDTO;

  const setNotification = useNotificationStore((state) => state.setNotification);

  const getJobs = useCallback(
    async (query = '', page = 0, numOfResults = 10, sortBy = 0) => {
      const jobSearchFilter: IJobSearchFilterDTO = {
        jobAssignmentStatusFilter: JobAssignmentStatusFilter.Assigned_Accepted,
        page: page,
        size: numOfResults,
        query: query,
        sort: sortBy === 0 ? ['postedDate,desc'] : ['postedDate,asc'],
        recruiterPartnerEmail: user?.email ?? '',
      };
      try {
        const response = await getAssignedJobs(jobSearchFilter);
        setJobs(response.data);
        setTotalJobsCount(parseInt(response.headers['x-total-count']));
      } catch (err: any) {
        setNotification(failed(err.message));
      }
    },
    [user, setNotification]
  );

  const handleJobSearch = async (data) =>
    getJobs(data.jobSearchQuery, 0, data.numOfResults, data.sortBy);

  const getJob = useCallback(
    (jobCodes: string[]) => {
      setJobCode(jobCodes[0]);
      setSelectedJob(jobs.filter((job) => job.jobCode === jobCodes[0])[0]);
    },
    [jobs]
  );

  const acceptJobAssignment = useCallback(
    async (assignment: IWorkAssignment, teamIdOrRecruiterId: string | undefined) => {
      try {
        if (teamIdOrRecruiterId) {
          await acceptWorkAssignment(assignment.id, teamIdOrRecruiterId);
          assignment.isAccepted = true;
          setNotification(success('Assignment accepted successfully. You can start working now.'));
          if (tempJob && tempJob.jobCode) {
            getJob([tempJob.jobCode]);
          }
          setState((j) => ({ ...j, tempSelectedJob: null }));
          return true;
        }
        return false;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    [getJob, setNotification, tempJob]
  );

  const validAssignment = useCallback(
    async (jobCodes: string[]) => {
      if (!isEmpty(jobCodes)) {
        const job = jobs.filter((job) => job.jobCode === jobCodes[0])[0];
        setTempJob(job);
        if (job && job.id) {
          const api = await getWorkAssignmentByCurrentRecruiterAndJob(job.id);
          const assignment: IWorkAssignment = api.data;
          if (!isEmpty(api.data)) {
            setState((j) => ({ ...j, assignmentData: assignment }));
            if (!assignment.isClosed) {
              if (!assignment.isAccepted) {
                const assignmentToTeams = keys(assignment.assignedToTeams);
                const assignmentByRecruiters = keys(assignment.assignedByRecruiters);
                setState((j) => ({
                  ...j,
                  assignmentToTeams: assignmentToTeams,
                  assignmentByRecruiters: assignmentByRecruiters,
                }));

                const totalAssignees = assignmentToTeams.length + assignmentByRecruiters.length;
                if (totalAssignees > 1) {
                  setState((j) => ({
                    ...j,
                    tempSelectedJob: job,
                    openJobAssignmentSelection: true,
                  }));
                } else if (totalAssignees == 1) {
                  return assignment?.team?.id
                    ? acceptJobAssignment(assignment, assignment.team?.id)
                    : acceptJobAssignment(assignment, assignmentByRecruiters[0]);
                } else {
                  const teamIdOrRecruiterId = !isEmpty(assignmentToTeams)
                    ? assignmentToTeams[0]
                    : assignmentByRecruiters[0];
                  return acceptJobAssignment(assignment, teamIdOrRecruiterId);
                }
              } else {
                getJob(jobCodes);
                return true;
              }
            } else {
              const jobOwner = job.team ? job.team.name : 'Job Owner';
              setNotification(
                warning(
                  `${jobOwner} didn't assign this job to your team.  To add candidates, please get this assigned to your team`
                )
              );
              handleClose();
              return false;
            }
          } else {
            const jobOwner = job.team ? job.team.name : 'Job Owner';
            setNotification(
              warning(
                `${jobOwner} didn't assign this job to your team.  To add candidates, please get this assigned to your team`
              )
            );
            handleClose();
            return false;
          }
        }
      }
    },
    [acceptJobAssignment, getJob, handleClose, jobs, setNotification]
  );

  useEffect(() => {
    defaultJob === null && getJobs();
  }, [defaultJob, getJobs]);

  const validateJob = async (jobCodes: string[]) => {
    const valid = await validAssignment(jobCodes);
    if (valid) {
      getJob(jobCodes);
    }
  };
  const selectedJobTitle = (jobTitle: string) => setSelectedTitle(jobTitle);
  return (
    <>
      {state.assignmentData !== null && state.openJobAssignmentSelection ? (
        <FormProvider {...methods}>
          <JobAssignmentSelection
            open={state.openJobAssignmentSelection}
            assignment={state.assignmentData}
            assignmentToTeams={state.assignmentToTeams}
            assignmentByRecruiters={state.assignmentByRecruiters}
            acceptJobAssignment={acceptJobAssignment}
            onClose={() => setState((j) => ({ ...j, openJobAssignmentSelection: true }))}
          />
        </FormProvider>
      ) : (
        <WsDrawer
          title={jobCode === null ? 'Select Job' : 'Bulk SMS'}
          open={open}
          onClose={handleClose}
          rightChildren={
            jobCode != null ? (
              <Grid container direction="row" spacing={2} pb={1}>
                <WsTypography variant={WsTypoVariants.TITLE}>{selectedJob?.title}</WsTypography>
                &nbsp;
                <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
                  {'(' + selectedJob?.jobCode + ')'}
                </WsTypography>
              </Grid>
            ) : (
              ''
            )
          }
        >
          {jobCode === null && (
            <JobSelection
              multiple={false}
              jobs={jobs}
              totalCount={totalJobsCount}
              getSelectedJobs={validateJob}
              selectedJobTitle={selectedJobTitle}
              handleJobSearch={handleJobSearch}
              handleChangeParameters={getJobs}
              handleClose={handleClose}
            />
          )}
          {jobCode !== null && (
            <BulkSms
              selectedCandidates={candidates}
              job={selectedJob ?? {}}
              jobTitle={selectedTitle ?? ''}
              handleClose={handleClose}
            />
          )}
        </WsDrawer>
      )}
    </>
  );
};

export default BulkSmsDrawer;
